/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { auth, isUsingMockKeys } from '../../firebase';
import { useWBL } from '../../context/WBLContext';
import { ShieldCheck, Lock, Eye, EyeOff, CheckCircle2, AlertCircle, Loader2, ArrowRight, KeyRound, ArrowLeft } from 'lucide-react';
import { GlobalFooter } from '../Common/GlobalFooter';

export const ResetPasswordPage: React.FC = () => {
  const { globalBranding } = useWBL();

  // Read URL parameters provided by Firebase action link or route
  const [searchParams] = useState(() => {
    if (typeof window !== 'undefined') {
      return new URLSearchParams(window.location.search);
    }
    return new URLSearchParams();
  });

  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');
  const apiKey = searchParams.get('apiKey');
  const continueUrl = searchParams.get('continueUrl');

  // Verification state
  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [accountEmail, setAccountEmail] = useState<string | null>(null);
  const [isMissingCode, setIsMissingCode] = useState(false);

  // Form input state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const primaryColor = globalBranding?.palette?.lightMain || '#0094c8';

  const navigateToLogin = () => {
    if (typeof window !== 'undefined') {
      if ((window as any).smoothNavigate) {
        (window as any).smoothNavigate('/login');
      } else {
        window.location.href = '/login';
      }
    }
  };

  const navigateToForgotPassword = () => {
    if (typeof window !== 'undefined') {
      if ((window as any).smoothNavigate) {
        (window as any).smoothNavigate('/forgot-password');
      } else {
        window.location.href = '/forgot-password';
      }
    }
  };

  // On mount, verify the action code (oobCode) with Firebase Auth
  useEffect(() => {
    let isMounted = true;

    async function checkCode() {
      // Validate mode parameter
      if (mode && mode !== 'resetPassword') {
        if (isMounted) {
          setVerificationError(`Invalid request action mode "${mode}". This page is intended for password reset verification.`);
          setIsVerifying(false);
        }
        return;
      }

      if (!oobCode) {
        if (isMounted) {
          if (isUsingMockKeys) {
            // Dev mode fallback when testing without parameters
            setAccountEmail('user@thespartanburgedge.com');
            setIsVerifying(false);
            return;
          }
          setIsMissingCode(true);
          setIsVerifying(false);
        }
        return;
      }

      if (isUsingMockKeys) {
        if (isMounted) {
          setAccountEmail('user@thespartanburgedge.com');
          setIsVerifying(false);
        }
        return;
      }

      try {
        const email = await verifyPasswordResetCode(auth, oobCode);
        if (isMounted) {
          setAccountEmail(email);
          setIsVerifying(false);
        }
      } catch (err: any) {
        console.error('Error verifying password reset code:', err);
        if (isMounted) {
          let msg = 'This password reset link is invalid or has expired. Password reset links are single-use and expire after a short period.';
          if (err.code === 'auth/expired-action-code') {
            msg = 'This password reset link has expired. Please request a new password reset email.';
          } else if (err.code === 'auth/invalid-action-code') {
            msg = 'This password reset link is invalid or has already been used. Please request a new reset email.';
          } else if (err.code === 'auth/user-disabled') {
            msg = 'The user account associated with this request has been disabled.';
          } else if (err.code === 'auth/user-not-found') {
            msg = 'No user account was found corresponding to this password reset code.';
          }
          setVerificationError(msg);
          setIsVerifying(false);
        }
      }
    }

    checkCode();

    return () => {
      isMounted = false;
    };
  }, [mode, oobCode]);

  // Handle password reset submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (newPassword.length < 8) {
      setSubmitError('Password must be at least 8 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setSubmitError('Passwords do not match. Please verify your entries.');
      return;
    }

    if (!oobCode && !isUsingMockKeys) {
      setSubmitError('Missing security code. Please request a new password reset email.');
      return;
    }

    setIsSubmitting(true);

    try {
      if (isUsingMockKeys) {
        await new Promise((resolve) => setTimeout(resolve, 800));
      } else {
        await confirmPasswordReset(auth, oobCode!, newPassword);
      }
      setResetSuccess(true);
    } catch (err: any) {
      console.error('Error confirming password reset:', err);
      let msg = err.message || 'Failed to update password. Please try again.';
      if (err.code === 'auth/expired-action-code') {
        msg = 'This reset link has expired. Please request a new password reset email.';
      } else if (err.code === 'auth/invalid-action-code') {
        msg = 'This reset link is invalid or has already been used.';
      } else if (err.code === 'auth/weak-password') {
        msg = 'Password is too weak. Please enter at least 8 characters with letters and numbers.';
      }
      setSubmitError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const passwordsMatch = newPassword.length > 0 && confirmPassword.length > 0 && newPassword === confirmPassword;
  const isLengthValid = newPassword.length >= 8;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col justify-between font-sans selection:bg-[#0094c8] selection:text-white relative overflow-x-hidden">
      {/* Background Glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-[400px] bg-gradient-to-b from-[#011d3c]/80 via-slate-950/40 to-transparent pointer-events-none -z-10 blur-3xl opacity-60" />

      {/* Header Bar */}
      <header className="border-b border-slate-800/80 bg-slate-950/80 backdrop-blur-md sticky top-0 z-50 px-4 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button 
            onClick={navigateToLogin}
            className="flex items-center space-x-3 cursor-pointer group text-left outline-none"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#011d3c] to-[#0094c8] p-0.5 shadow-lg shadow-[#0094c8]/20 group-hover:scale-105 transition-all">
              <div className="w-full h-full bg-slate-950 rounded-[10px] flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-[#0094c8]" />
              </div>
            </div>
            <div>
              <h1 className="text-base font-black tracking-tight text-white uppercase leading-none">
                The Spartanburg Edge
              </h1>
              <p className="text-[10px] font-bold text-slate-400 tracking-widest uppercase mt-0.5">
                Work-Based Learning Portal
              </p>
            </div>
          </button>

          <button
            onClick={navigateToLogin}
            className="flex items-center space-x-2 text-xs font-bold text-slate-400 hover:text-white transition-colors cursor-pointer px-3 py-1.5 rounded-lg border border-slate-800 hover:border-slate-700 bg-slate-900/50"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>Return to Login</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4 lg:p-8 my-auto z-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          className="max-w-md w-full bg-slate-900/90 border border-slate-800 rounded-3xl shadow-2xl shadow-slate-950/80 overflow-hidden backdrop-blur-xl"
        >
          {/* Card Top Banner */}
          <div className="bg-[#011d3c] p-6 lg:p-8 text-white relative overflow-hidden border-b border-slate-800">
            <div className="absolute top-0 right-0 p-8 opacity-10 rotate-12 pointer-events-none">
              <ShieldCheck className="w-32 h-32 text-white" />
            </div>
            <div className="relative z-10 space-y-2">
              <div className="inline-flex items-center space-x-2 px-2.5 py-1 rounded-full bg-[#0094c8]/20 border border-[#0094c8]/30 text-[#0094c8] text-[10px] font-extrabold uppercase tracking-widest">
                <Lock className="w-3.5 h-3.5" />
                <span>Security Credentials</span>
              </div>
              <h2 className="text-2xl font-black uppercase tracking-tight text-white mt-1">
                {isMissingCode ? 'Credentials Updated' : 'Create New Password'}
              </h2>
              {accountEmail && (
                <div className="pt-1">
                  <p className="text-[11px] text-slate-300 font-medium">Resetting credentials for account:</p>
                  <p className="text-xs font-bold text-[#0094c8] font-mono break-all">{accountEmail}</p>
                </div>
              )}
            </div>
          </div>

          {/* Card Body States */}
          <div className="p-6 lg:p-8">
            {isVerifying ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-4 text-center">
                <Loader2 className="w-10 h-10 text-[#0094c8] animate-spin" />
                <div className="space-y-1">
                  <p className="text-sm font-bold text-white uppercase tracking-wider">Verifying Link Security...</p>
                  <p className="text-xs text-slate-400">Validating password reset token with Firebase Auth</p>
                </div>
              </div>
            ) : isMissingCode ? (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center space-y-6 py-2"
              >
                <div className="w-16 h-16 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl flex items-center justify-center mx-auto text-emerald-400 shadow-lg shadow-emerald-500/10">
                  <CheckCircle2 className="w-8 h-8" />
                </div>

                <div className="space-y-2">
                  <h3 className="text-xl font-black text-white uppercase tracking-tight">
                    PASSWORD RESET COMPLETED!
                  </h3>
                  <p className="text-slate-300 text-xs leading-relaxed max-w-sm mx-auto">
                    If you just updated your password via the secure verification page, your new credentials have been successfully updated on the portal.
                  </p>
                  <p className="text-[11px] text-slate-400 leading-relaxed pt-1">
                    You can now proceed and sign in with your new password.
                  </p>
                </div>

                <div className="pt-2">
                  <button
                    onClick={navigateToLogin}
                    style={{ backgroundColor: primaryColor }}
                    className="w-full py-4 px-6 text-white rounded-xl font-bold uppercase tracking-wider text-xs transition-all shadow-lg hover:brightness-110 flex items-center justify-center space-x-2 cursor-pointer"
                  >
                    <span>Sign In to Portal</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ) : verificationError ? (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center space-y-6 py-2"
              >
                <div className="w-16 h-16 bg-red-500/10 border border-red-500/30 rounded-2xl flex items-center justify-center mx-auto text-red-400 shadow-lg shadow-red-500/10">
                  <AlertCircle className="w-8 h-8" />
                </div>

                <div className="space-y-2">
                  <h3 className="text-xl font-black text-white uppercase tracking-tight">
                    Link Invalid or Expired
                  </h3>
                  <p className="text-xs text-red-300 bg-red-950/40 p-4 rounded-xl border border-red-800/60 leading-relaxed text-left">
                    {verificationError}
                  </p>
                </div>

                <div className="pt-2 space-y-3">
                  <button
                    onClick={navigateToForgotPassword}
                    style={{ backgroundColor: primaryColor }}
                    className="w-full py-3.5 px-6 text-white rounded-xl font-bold uppercase tracking-wider text-xs transition-all shadow-lg hover:brightness-110 flex items-center justify-center space-x-2 cursor-pointer"
                  >
                    <KeyRound className="w-4 h-4" />
                    <span>Request New Reset Link</span>
                  </button>

                  <button
                    onClick={navigateToLogin}
                    className="w-full py-2.5 text-xs text-slate-400 hover:text-white font-medium transition-colors cursor-pointer"
                  >
                    Return to Sign In
                  </button>
                </div>
              </motion.div>
            ) : resetSuccess ? (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center space-y-6 py-2"
              >
                <div className="w-16 h-16 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl flex items-center justify-center mx-auto text-emerald-400 shadow-lg shadow-emerald-500/10">
                  <CheckCircle2 className="w-8 h-8" />
                </div>

                <div className="space-y-2">
                  <h3 className="text-xl font-black text-white uppercase tracking-tight">
                    Password Reset Complete
                  </h3>
                  <p className="text-slate-300 text-xs leading-relaxed max-w-sm mx-auto">
                    Your account password has been updated successfully. You can now log in using your new credentials.
                  </p>
                </div>

                <div className="pt-2">
                  <button
                    onClick={navigateToLogin}
                    style={{ backgroundColor: primaryColor }}
                    className="w-full py-4 px-6 text-white rounded-xl font-bold uppercase tracking-wider text-xs transition-all shadow-lg hover:brightness-110 flex items-center justify-center space-x-2 cursor-pointer"
                  >
                    <span>Sign In Now</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {submitError && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-red-950/50 border border-red-800/80 rounded-xl flex items-start space-x-3"
                  >
                    <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                    <p className="text-xs text-red-200 font-medium leading-relaxed">
                      {submitError}
                    </p>
                  </motion.div>
                )}

                <div className="space-y-4">
                  {/* New Password */}
                  <div className="space-y-1.5">
                    <label className="block text-[11px] font-black uppercase tracking-wider text-slate-300">
                      New Password <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        required
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Minimum 8 characters"
                        className="w-full pl-11 pr-11 py-3.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#0094c8] focus:ring-1 focus:ring-[#0094c8] transition-all font-medium"
                      />
                      <Lock className="w-5 h-5 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors p-1"
                        aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div className="space-y-1.5">
                    <label className="block text-[11px] font-black uppercase tracking-wider text-slate-300">
                      Confirm New Password <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        required
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Repeat new password"
                        className="w-full pl-11 pr-11 py-3.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#0094c8] focus:ring-1 focus:ring-[#0094c8] transition-all font-medium"
                      />
                      <Lock className="w-5 h-5 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors p-1"
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Password Requirement Indicators */}
                  <div className="p-3 bg-slate-950/60 rounded-xl border border-slate-800/80 space-y-1.5 text-[11px]">
                    <div className="flex items-center space-x-2">
                      <div className={`w-1.5 h-1.5 rounded-full ${isLengthValid ? 'bg-emerald-400' : 'bg-slate-600'}`} />
                      <span className={isLengthValid ? 'text-emerald-400 font-semibold' : 'text-slate-400'}>
                        At least 8 characters
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className={`w-1.5 h-1.5 rounded-full ${passwordsMatch ? 'bg-emerald-400' : 'bg-slate-600'}`} />
                      <span className={passwordsMatch ? 'text-emerald-400 font-semibold' : 'text-slate-400'}>
                        Passwords match
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || !isLengthValid || !passwordsMatch}
                  style={{ backgroundColor: primaryColor }}
                  className="w-full py-4 text-white rounded-xl font-bold uppercase tracking-wider text-xs transition-all shadow-lg hover:brightness-110 active:scale-[0.99] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center space-x-2 cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin text-white" />
                      <span>Updating Credentials...</span>
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="w-4 h-4" />
                      <span>Reset Account Password</span>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <GlobalFooter />
    </div>
  );
};
